/**
 * Analytics event helpers — wraps @vercel/analytics `track()` so the rest of
 * the codebase calls a single typed function and we can swap providers later.
 *
 * Keep event names in snake_case and under 50 chars (Vercel limit).
 * Keep prop values as strings / numbers / booleans only.
 */

import { track as vercelTrack } from "@vercel/analytics";
import { isAnalyticsGranted } from "@/lib/consent";

export type AnalyticsEvent =
  // Hero + global CTAs
  | "cta_try_naas_configurator"
  | "cta_explore_network"
  | "cta_contact_us"
  // Service pages
  | "service_view_all_services"
  | "service_page_city_click"
  // Locations
  | "location_card_click"
  | "location_contact_sales"
  // Forms
  | "contact_form_submit"
  | "newsletter_subscribe"
  // AI + tools
  | "ai_advisor_open"
  | "ai_advisor_message_sent"
  | "latency_checker_run"
  | "roi_calculator_run"
  // Nav
  | "header_nav_click"
  | "footer_link_click"
  // A/B testing
  | "ab_variant_exposure"
  // Configurator internal (Solution Advisor) — emitted from UnifiedConfigurator
  | "sa_step_advance"
  | "sa_step_back"
  | "sa_reset"
  | "sa_product_select"
  | "sa_bandwidth_select"
  | "sa_quote_submit"
  | "sa_quote_success"
  | "sa_quote_error"
  | "sa_send_to_contact"
  | "sa_pdf_download"
  | "sa_link_copied"
  | "sa_compare_save"
  // Sprint #2 — funnel tracking
  | "sa_step_abandon"
  | "sa_step_view"
  | "mini_pricer_submit"
  | "mini_pricer_interact";

type Props = Record<string, string | number | boolean | null | undefined>;

/**
 * Sprint #2 — Read or create a session ID from sessionStorage.
 * Returns empty string in SSR or when sessionStorage is unavailable.
 */
export function getSessionId(): string {
  if (typeof window === "undefined") return "";
  // Session id is only used to correlate analytics events across steps.
  // Do not generate or persist it without analytics consent.
  if (!isAnalyticsGranted()) return "";
  try {
    let sid = sessionStorage.getItem("sa_session_id");
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem("sa_session_id", sid);
    }
    return sid;
  } catch {
    return "";
  }
}

/**
 * Fire a tracked event. Swallows errors — analytics should never break UX.
 * No-op during SSR / when script hasn't loaded yet.
 * Sprint #2: automatically merges session_id into every sa_* event.
 */
export function trackEvent(event: AnalyticsEvent, props?: Props): void {
  try {
    if (typeof window === "undefined") return;
    if (!isAnalyticsGranted()) return;
    // Strip undefined so Vercel doesn't reject payload
    const clean: Props = {};
    if (props) {
      for (const [k, v] of Object.entries(props)) {
        if (v !== undefined && v !== null) clean[k] = v;
      }
    }
    // Sprint #2 — auto-merge session_id for all sa_* events
    if (event.startsWith("sa_")) {
      const sid = getSessionId();
      if (sid) clean.session_id = sid;
    }
    vercelTrack(event, clean as Record<string, string | number | boolean | null>);
  } catch {
    // silent — do not block UX if analytics fail
  }
}
